<template>
	<FormCard>
		<Chart v-if="showPieChart" type="pie" :data="pieChartData" :options="pieOptions" class="w-full" />

		<Chart v-else type="bar" :data="barChartData" :options="barOptions" class="w-full" />
	</FormCard>
</template>

<script setup lang="ts">
import { computed } from "vue"
import Chart from "primevue/chart"

const props = defineProps({
	showPieChart: Boolean,
	finalResult: { type: Object, default: () => ({}) },
	finalResults: { type: Array, default: () => [] },
	labelText: { type: String, default: "" },
	dataKey: { type: String, required: true },
	emissionFactorName: { type: String, default: "" },
})

const colors = ["#C30606", "#FFCF27", "#3896EC", "#D97706", "#7C3AED", "#0891B2", "#BE185D"]

/*
Helper functions
*/
function getLabel(row: any) {
	return row.stageName || row.stepName || row.materialName || row.name
}

function getValue(row: any) {
	return row.emissionFactors?.[props.emissionFactorName] ?? 0;
}

function getRows(result: any) {
	return (result?.[props.dataKey] ?? []).filter((r: any) => getLabel(r)?.toLowerCase?.() !== "total")
}

/*
PIE CHART
(single result)
*/
const pieChartData = computed(() => {
	const rows = getRows(props.finalResult)
	return {
		labels: rows.map(getLabel),
		datasets: [
			{
				data: rows.map(getValue),
				backgroundColor: rows.map((_, index) => colors[index % colors.length]),
				borderWidth: 0,
			},
		],
	}
})

const pieOptions = {
	plugins: {
		legend: { position: "bottom" },
		title: {
			display: true,
			text: props.labelText,
		},
	},
}

/*
STACKED BAR CHART
(compare multiple results)
*/
const barChartData = computed(() => {
	if (!props.finalResults?.length) return { labels: [], datasets: [] }

	const labels = props.finalResults.map((r: any) => r.label)

	const categories = [...new Set(props.finalResults.flatMap((r: any) => getRows(r.result).map(getLabel)))]

	const datasets = categories.map((category: string, index: number) => ({
		label: category,
		stack: "total",
		data: props.finalResults.map((r: any) => {
			const row = getRows(r.result).find((x: any) => getLabel(x) === category)
			return row ? getValue(row) : 0
		}),
		backgroundColor: colors[index % colors.length],
		borderColor: colors[index % colors.length],
		borderWidth: 0,
	}))

	return { labels, datasets }
})

const barOptions = {
	responsive: true,
	plugins: {
		legend: { position: "left" },
	},
	scales: {
		x: { stacked: true },
		y: {
			stacked: true,
			title: {
				display: true,
				text: "kg CO2 eq.",
			},
		},
	},
	elements: {
		bar: {
			borderWidth: 0,
		},
	},
}
</script>
